import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import FidesChatAdminService, {
  AdminConversation,
} from '../../services/admin/FidesChatAdminService';
import type { ChatLastParticipantAddedActivity } from '../../utils/chatParticipantAddedNote';
import {
  getFidesChatFirestore,
  FIDES_CHAT_APP_NAME,
  CONVERSATIONS_COLLECTION,
} from '../../services/admin/FidesChatFirebase';

export const FIDES_CHAT_REFRESH_EVENT = 'fides-chat-refresh';

const POLL_INTERVAL_MS = 5_000;
const SNAPSHOT_MAX_ATTEMPTS = 3;
const SNAPSHOT_RETRY_DELAY_MS = 5_000;

function normalizeFirestoreDate(value: unknown): string | null {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  return null;
}

function normalizeLastActivity(
  value: unknown,
): ChatLastParticipantAddedActivity | null | undefined {
  if (value === undefined) return undefined;
  if (!value || typeof value !== 'object') return null;
  const activity = value as Partial<ChatLastParticipantAddedActivity>;
  if (activity.type !== 'participant_added') return null;
  if (!activity.inviteeAdminExternalId || !activity.inviteeName) return null;
  return {
    type: 'participant_added',
    inviterAdminExternalId: activity.inviterAdminExternalId ?? '',
    inviterName: activity.inviterName ?? '',
    inviteeAdminExternalId: activity.inviteeAdminExternalId,
    inviteeName: activity.inviteeName,
  };
}

function applyFirestoreUpdate(
  item: AdminConversation,
  data: Record<string, unknown>,
): AdminConversation {
  const lastActivity = normalizeLastActivity(data.lastActivity);
  return {
    ...item,
    unreadByAdmin:
      typeof data.unreadByAdmin === 'boolean' ? data.unreadByAdmin : item.unreadByAdmin,
    lastMessageText:
      typeof data.lastMessageText === 'string' ? data.lastMessageText : item.lastMessageText,
    lastMessageAt: normalizeFirestoreDate(data.lastMessageAt) ?? item.lastMessageAt,
    lastActivity: lastActivity === undefined ? item.lastActivity : lastActivity,
  };
}

export default function useFidesChatInbox(enabled = true) {
  const [conversations, setConversations] = useState<AdminConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const conversationsRef = useRef<AdminConversation[]>([]);
  conversationsRef.current = conversations;

  // Whether Firestore snapshot is active — when true, polling is suppressed
  const snapshotActiveRef = useRef(false);

  const fetchAll = useCallback(() => {
    return FidesChatAdminService.listConversations('open')
      .then((list) => {
        setConversations(list);
        setError(null);
      })
      .catch((err) => {
        console.error('Fides chat: failed to load conversations', err);
        setError('Unable to load chat conversations.');
      });
  }, []);

  // Initial load
  useEffect(() => {
    if (!enabled) {
      setConversations([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    void fetchAll().finally(() => setLoading(false));
  }, [enabled, fetchAll]);

  // Polling — runs every 10s but is suppressed once snapshot is active
  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setInterval(() => {
      if (!snapshotActiveRef.current) void fetchAll();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled, fetchAll]);

  // Firestore snapshot — attempt up to SNAPSHOT_MAX_ATTEMPTS times, then give up (polling takes over)
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const unsubscribers: Array<() => void> = [];
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const setup = async (attempt: number) => {
      try {
        const firestore = await getFidesChatFirestore();
        if (cancelled) return;

        let adminUid: string | null = null;
        try {
          const auth = getAuth(getApp(FIDES_CHAT_APP_NAME));
          adminUid = auth.currentUser?.uid ?? null;
        } catch {
          /* ignore */
        }

        const handleSnapshot = (snap: any) => {
          snapshotActiveRef.current = true;
          const knownIds = new Set(conversationsRef.current.map((c) => c.conversationId));
          let hasNew = false;
          const removedIds: string[] = [];

          snap.docChanges().forEach((change: any) => {
            const id: string = change.doc.id;
            const data = change.doc.data() as Record<string, unknown>;

            if (change.type === 'removed') {
              removedIds.push(id);
              return;
            }
            if (change.type === 'added' && !knownIds.has(id)) {
              hasNew = true;
              return;
            }
            if (change.type === 'modified' || change.type === 'added') {
              if (data.status === 'resolved') {
                removedIds.push(id);
                return;
              }
              setConversations((current) =>
                current.map((item) =>
                  item.conversationId === id ? applyFirestoreUpdate(item, data) : item,
                ),
              );
            }
          });

          if (removedIds.length > 0) {
            const removedSet = new Set(removedIds);
            setConversations((current) =>
              current.filter((item) => !removedSet.has(item.conversationId)),
            );
          }

          if (hasNew) {
            void FidesChatAdminService.listConversations('open')
              .then((all) => {
                setConversations((current) => {
                  const existingIds = new Set(current.map((c) => c.conversationId));
                  const toAdd = all.filter((c) => !existingIds.has(c.conversationId));
                  return toAdd.length > 0 ? [...current, ...toAdd] : current;
                });
              })
              .catch(() => {});
          }
        };

        unsubscribers.push(
          onSnapshot(
            query(
              collection(firestore, CONVERSATIONS_COLLECTION),
              where('ownerAdminUid', '==', null),
            ),
            handleSnapshot,
            (err) => {
              console.warn('Fides chat snapshot error', err);
              snapshotActiveRef.current = false;
            },
          ),
        );

        if (adminUid) {
          unsubscribers.push(
            onSnapshot(
              query(
                collection(firestore, CONVERSATIONS_COLLECTION),
                where('participantAdminUids', 'array-contains', adminUid),
              ),
              handleSnapshot,
              (err) => {
                console.warn('Fides chat snapshot error', err);
                snapshotActiveRef.current = false;
              },
            ),
          );
        }
      } catch (err) {
        if (cancelled) return;
        if (attempt < SNAPSHOT_MAX_ATTEMPTS - 1) {
          console.warn(
            `Fides chat: snapshot setup failed (attempt ${attempt + 1}/${SNAPSHOT_MAX_ATTEMPTS}), retrying in ${SNAPSHOT_RETRY_DELAY_MS / 1000}s`,
            err,
          );
          retryTimer = setTimeout(() => {
            void setup(attempt + 1);
          }, SNAPSHOT_RETRY_DELAY_MS);
        } else {
          console.warn(
            'Fides chat: snapshot unavailable after retries, falling back to polling',
            err,
          );
          // polling is already running — nothing more to do
        }
      }
    };

    void setup(0);

    return () => {
      cancelled = true;
      snapshotActiveRef.current = false;
      if (retryTimer) clearTimeout(retryTimer);
      unsubscribers.forEach((u) => u());
    };
  }, [enabled]);

  // External refresh event (e.g. after resolve/claim)
  useEffect(() => {
    if (!enabled) return undefined;
    const handle = () => {
      void fetchAll();
    };
    window.addEventListener(FIDES_CHAT_REFRESH_EVENT, handle);
    return () => window.removeEventListener(FIDES_CHAT_REFRESH_EVENT, handle);
  }, [enabled, fetchAll]);

  const refresh = useCallback(() => {
    void fetchAll();
  }, [fetchAll]);

  const sortedConversations = useMemo(
    () =>
      [...conversations].sort((left, right) => {
        const leftTime = left.lastMessageAt ? new Date(left.lastMessageAt).getTime() : 0;
        const rightTime = right.lastMessageAt ? new Date(right.lastMessageAt).getTime() : 0;
        return rightTime - leftTime;
      }),
    [conversations],
  );

  return { conversations: sortedConversations, loading, error, refresh };
}
